import { WakeupCallsPage } from './pages/WakeupCallsPage';
import { OperationReportPage } from './pages/OperationReportPage';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { api, ApiError, type Me } from './api/client';
import { Layout } from './app/Layout';
import { SessionProvider } from './app/session';
import { Loading } from './components/ui';
import { AccountPage } from './pages/AccountPage';
import { UserEditPage, UsersPage } from './pages/UsersPage';
import { BrandingPage } from './pages/BrandingPage';
import { HomeDesignerPage } from './pages/HomeDesignerPage';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import { LiveTvPage } from './pages/LiveTvPage';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages';
import { PackagesPage } from './pages/PackagesPage';
import { SettingsPage } from './pages/SettingsPage';
import { SitesPage } from './pages/SitesPage';
import { TvMenuPage } from './pages/TvMenuPage';
import { AppsPage, FacilitiesPage, HotelInfoPage, RestaurantsPage } from './pages/GuestContentPages';
import { VenueMenuPage } from './pages/VenueMenuPage';
import { MediaLibraryPage } from './pages/MediaLibraryPage';
import { VodPage } from './pages/VodPage';
import { AodPage } from './pages/AodPage';
import { AodEditPage } from './pages/AodEditPage';
import { VodEditPage } from './pages/VodEditPage';
import { GuestsPage, IntegrationsPage } from './pages/GuestsPage';
import { MarketingPage } from './pages/MarketingPage';
import { EmergencyPage } from './pages/EmergencyPage';
import { CampaignPage } from './pages/CampaignPage';
import { FacilityEditPage } from './pages/FacilityEditPage';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: true },
  },
});

function Authenticated() {
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/auth/me'), retry: false });
  useEffect(() => {
    const onUnauthorized = () => navigate('/login');
    window.addEventListener('pulseiptv:unauthorized', onUnauthorized);
    return () => window.removeEventListener('pulseiptv:unauthorized', onUnauthorized);
  }, [navigate]);
  if (me.isLoading) return <Loading />;
  if (me.error || !me.data) return <Navigate to="/login" replace />;
  return (
    <SessionProvider me={me.data}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<DashboardPage />} />
          <Route path="content/live-tv" element={<LiveTvPage />} />
          <Route path="content/packages" element={<PackagesPage />} />
          <Route path="content/vod" element={<VodPage />} />
          <Route path="content/vod/:id" element={<VodEditPage />} />
          <Route path="content/aod" element={<AodPage />} />
          <Route path="content/aod/:id" element={<AodEditPage />} />
          <Route path="experience/facilities" element={<FacilitiesPage />} />
          <Route path="experience/facilities/:id" element={<FacilityEditPage />} />
          <Route path="experience/restaurants" element={<RestaurantsPage />} />
          <Route path="experience/restaurants/:venueId/menu" element={<VenueMenuPage />} />
          <Route path="experience/info" element={<HotelInfoPage />} />
          <Route path="experience/apps" element={<AppsPage />} />
          <Route path="experience/tv-menu" element={<TvMenuPage />} />
          <Route path="experience/welcome" element={<BrandingPage />} />
          <Route path="experience/home-design" element={<HomeDesignerPage />} />
          <Route path="sites" element={<SitesPage />} />
          <Route path="devices" element={<DevicesPage />} />
          <Route path="devices/report" element={<OperationReportPage />} />
          {/* Wake-up calls are set per room TV, in the device panel. */}
          <Route path="wake-up-calls" element={<WakeupCallsPage />} />
          <Route path="marketing" element={<MarketingPage />} />
          <Route path="marketing/campaigns/new" element={<CampaignPage />} />
          <Route path="marketing/campaigns/:id" element={<CampaignPage />} />
          <Route path="guests" element={<GuestsPage />} />
          <Route path="emergency" element={<EmergencyPage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="account/users" element={<UsersPage />} />
          <Route path="account/users/:id" element={<UserEditPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/*" element={<Authenticated />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
